import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/apiClient";

// Fallback locale (usato se l'API non risponde). Fonte unica: GET /public/compliance-notes (modificabile dall'Admin).
export const COMPLIANCE_NOTES = [
  { key: "informativa", label: "Informativa generale", text: "Questa pagina ha finalità esclusivamente informativa e non costituisce offerta al pubblico né vendita a distanza." },
  { key: "ivass", label: "Tutela IVASS", text: "Ogni attività di intermediazione è svolta da un intermediario iscritto al RUI. Nessuna proposta o preventivo viene generato automaticamente." },
  { key: "privacy", label: "Tutela privacy", text: "I dati raccolti sono utilizzati solo per rispondere alla richiesta informativa e non sono ceduti a terzi." },
  { key: "ai", label: "Trasparenza IA (AI Act art. 50)", text: "Alcuni contenuti (testi, sintesi, audio a voce sintetica) sono generati con l'ausilio di sistemi di Intelligenza Artificiale e sono chiaramente segnalati come tali. Nessuna decisione automatizzata viene assunta sul cliente: valutazione e assunzione del rischio sono sempre effettuate da personale umano qualificato." },
  { key: "commerciale", label: "Tutela commerciale", text: "Le informazioni non costituiscono raccomandazione personalizzata. Per una valutazione adeguata è necessario un contatto diretto con un consulente." },
];

let _cache = null;
function fetchPayload() {
  if (!_cache) {
    _cache = api.get("/public/compliance-notes")
      .then(({ data }) => ({
        notes: Array.isArray(data?.notes) && data.notes.length ? data.notes : COMPLIANCE_NOTES,
        adesione_notice: data?.adesione_notice || "",
      }))
      .catch(() => ({ notes: COMPLIANCE_NOTES, adesione_notice: "" }));
  }
  return _cache;
}

export function useComplianceNotes() {
  const [notes, setNotes] = useState(COMPLIANCE_NOTES);
  useEffect(() => {
    let mounted = true;
    fetchPayload().then((p) => { if (mounted) setNotes(p.notes); });
    return () => { mounted = false; };
  }, []);
  return notes;
}

export function useAdesioneNotice() {
  const [notice, setNotice] = useState("");
  useEffect(() => {
    let mounted = true;
    fetchPayload().then((p) => { if (mounted) setNotice(p.adesione_notice); });
    return () => { mounted = false; };
  }, []);
  return notice;
}

export function AdesioneNotice({ className = "" }) {
  const notice = useAdesioneNotice();
  if (!notice) return null;
  return (
    <div className={`rounded-xl border border-forest/15 bg-[#F0F4F1] p-4 ${className}`} data-testid="adesione-notice">
      <div className="flex items-center gap-2 text-forest">
        <ShieldCheck className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Nota legale — Vendita a distanza (IVASS)</span>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{notice}</p>
    </div>
  );
}

// variant: "block" (landing, evidenza) | "footer" (app/gestionale, compatto)
export function ComplianceNotice({ variant = "block", className = "" }) {
  const notes = useComplianceNotes();

  if (variant === "footer") {
    return (
      <div className={`border-t border-border/60 bg-white/60 ${className}`} data-testid="compliance-notice">
        <div className="mx-auto max-w-6xl px-4 py-4 md:px-6">
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {notes.map((n, i) => (
              <span key={n.key || n.label}>
                <span className="font-medium text-forest/80">{n.label}:</span> {n.text}
                {i < notes.length - 1 ? "  ·  " : ""}
              </span>
            ))}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={`rounded-xl border border-forest/15 bg-[#F0F4F1] p-5 ${className}`} data-testid="compliance-notice">
      <div className="flex items-center gap-2 text-forest">
        <ShieldCheck className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Note informative e di compliance</span>
      </div>
      <ul className="mt-3 space-y-2">
        {notes.map((n) => (
          <li key={n.key || n.label} className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-forest/80">{n.label}.</span> {n.text}
          </li>
        ))}
      </ul>
    </div>
  );
}
